import React, { useState, useCallback } from "react";
import { X } from "lucide-react";
import api from "../../../lib/api";

export const STATUS = {
  in_review: { label: "In Review", cls: "bg-gold/20 text-gold-deep" },
  changes_requested: { label: "Changes Requested", cls: "bg-garnet/12 text-garnet" },
  draft: { label: "Draft", cls: "bg-sapphire-deep/10 text-sapphire-deep" },
  published: { label: "Published", cls: "bg-emerald/12 text-emerald" },
  archived: { label: "Unpublished", cls: "bg-stonegrey/15 text-stonegrey" },
};

export function itemStatus(item) {
  if (item.archived && !item.draft) return "archived";
  return item.status;
}

export function StatusBadge({ status, testid }) {
  const s = STATUS[status] || STATUS.draft;
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 font-sans text-[11px] font-semibold uppercase tracking-wide ${s.cls}`} data-testid={testid}>
      {s.label}
    </span>
  );
}

export function bytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export const MAX_UPLOAD = 15 * 1024 * 1024;

export async function uploadMedia(file) {
  if (file.size > MAX_UPLOAD) throw new Error(`${file.name} exceeds the 15 MB limit`);
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/cms/media", fd);
  return data;
}

export function Modal({ title, onClose, children, wide = false, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-sapphire-deep/40 p-4 backdrop-blur-sm" data-testid={testid}>
      <div className={`max-h-[90vh] w-full overflow-y-auto rounded-2xl bg-white p-7 shadow-heritage ${wide ? "max-w-4xl" : "max-w-lg"}`}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl font-semibold text-sapphire-deep">{title}</h3>
          <button onClick={onClose} className="text-ink/50 transition-colors hover:text-garnet" data-testid={`${testid}-close`} aria-label="Close">
            <X size={22} />
          </button>
        </div>
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}

export function useFlash() {
  const [msg, setMsg] = useState(null);
  const flash = useCallback((text, tone = "ok") => {
    setMsg({ text, tone });
    setTimeout(() => setMsg(null), 3500);
  }, []);
  const node = msg ? (
    <div
      className={`fixed bottom-6 right-6 z-[60] rounded-xl px-5 py-3 font-sans text-sm shadow-heritage ${
        msg.tone === "error" ? "bg-garnet text-parchment" : "bg-sapphire-deep text-parchment"
      }`}
      data-testid="cms-flash"
    >
      {msg.text}
    </div>
  ) : null;
  return [node, flash];
}

export const errText = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";

export const notifyCmsChanged = () => window.dispatchEvent(new Event("cms:changed"));

export const Label = ({ children }) => (
  <span className="mb-1.5 block font-sans text-xs font-semibold uppercase tracking-widest text-stonegrey">{children}</span>
);
